import {
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MobilePanelList from 'components/lists/MobilePanelList';
import { AppContext } from 'context/appContext';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  useFetcher,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useLoaderData,
  ActionFunction,
} from 'react-router';
import { ROUTES, routeWithParams } from 'router';
import {
  EntityTypes,
  InviteStatus,
  Permit,
  PermitStatus,
  PanelRootContext,
  Property,
  PropertyGroup,
  PermitType,
  PPIPusherEvent,
  TraxeroActionResponse,
} from 'types';
import Pusher from 'pusher-js';
import {
  formatLicensePlateString,
  formatVehicleString,
  formatLabel,
  formatDateRangeReportFileName,
  generatePermitPDF,
  generateCSV,
  THE_DISTANT_FUTURE,
  calculateEndOfTimePeriod,
  getBlankPermit,
  logError,
} from 'utils';
import PermitCancelDialog from './PermitRevokeDialog';
import PermitInviteDialog from './PermitInviteDialog';

import { MenuOption } from 'components/buttons/TableCellMenuButton';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  MRT_Row,
  MRT_SortingState,
  MRT_RowVirtualizer,
} from 'material-react-table';
import { useDefaultTableOptions } from 'hooks/useDefaultTableOptions';
import {
  Add,
  Approval,
  AutoAwesome,
  Cancel,
  FileDownload,
  History,
  Send,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import MRTableToolbar from 'components/tables/PanelTableToolbar';
import PermitReviewDialog from './PermitReviewDialog';
import { ListItemMenuButton } from 'components/buttons/ListItemMenuButton';
import { ToastContext, TOAST_TYPE } from 'context/toastContext';

import {
  getEntityPermitTypes,
  getPermitsByEntityID,
  sendPermitInvite,
  resendPermitInvite,
  sendSelfServiceLink,
  revokePermit,
  reviewPermit,
} from 'api';
import { useFetcherKey } from 'hooks/useFetcherKey';
export interface PropertyPermitsLoaderData {
  _permits: Promise<Permit[]>;
  _permitTypes: Promise<PermitType[]>;
}
export enum PermitTableActionHandler {
  SEND_INVITE = 'SEND INVITE',
  RESEND_INVITE = 'RESEND INVITE',
  SEND_SELF_SERVICE_LINK = 'SEND SELF SERVICE LINK',
  REVOKE_PERMIT = 'REVOKE PERMIT',
  APPROVE_PERMIT = 'APPROVE PERMIT',
  DENY_PERMIT = 'DENY PERMIT',
  UNKNOWN = 'UNKNOWN',
}
export interface PermitTableRouteParams {
  params: {
    tenantID?: string | undefined;
    propertyID?: string | undefined;
    groupID?: string | undefined;
  };
}

export async function permitTableLoader({
  params,
}: PermitTableRouteParams): Promise<PropertyPermitsLoaderData> {
  if (params.propertyID) {
    return {
      _permits: getPermitsByEntityID({
        tenantID: params.tenantID,
        entityType: EntityTypes.Property,
        entityID: params.propertyID,
        includeInactive: false,
      }),
      _permitTypes: getEntityPermitTypes({
        tenantID: params.tenantID,
        entityType: EntityTypes.Property,
        entityID: params.propertyID,
      }),
    };
  }

  if (params.groupID) {
    return {
      _permits: getPermitsByEntityID({
        tenantID: params.tenantID,
        entityType: EntityTypes.PropertyGroup,
        entityID: params.groupID,
        includeInactive: false,
      }),
      _permitTypes: getEntityPermitTypes({
        tenantID: params.tenantID,
        entityType: EntityTypes.PropertyGroup,
        entityID: params.groupID,
      }),
    };
  }

  return {
    _permits: Promise.resolve([]),
    _permitTypes: Promise.resolve([]),
  };
}
export const permitTableAction: ActionFunction = async ({
  request,
}): Promise<TraxeroActionResponse<PermitTableActionHandler>> => {
  const {
    body,
    handler,
    permitTypeID,
    permitID,
    entityType,
    entityID,
    tenantID,
  } = await request.json();

  let res: Response;
  try {
    switch (handler) {
      case PermitTableActionHandler.SEND_INVITE:
        res = await sendPermitInvite({
          tenantID,
          entityType,
          entityID,
          permitTypeID,
          body,
        });
        break;
      case PermitTableActionHandler.RESEND_INVITE:
        res = await resendPermitInvite({
          tenantID,
          entityType,
          entityID,
          permitTypeID,
          permitID,
          body,
        });
        break;
      case PermitTableActionHandler.SEND_SELF_SERVICE_LINK:
        res = await sendSelfServiceLink({
          tenantID,
          entityType,
          entityID,
          body,
        });
        break;
      case PermitTableActionHandler.REVOKE_PERMIT:
        res = await revokePermit({
          tenantID,
          entityType,
          entityID,
          permitID,
          body,
        });
        break;
      case PermitTableActionHandler.APPROVE_PERMIT:
      case PermitTableActionHandler.DENY_PERMIT:
        res = await reviewPermit({
          tenantID,
          entityType,
          entityID,
          permitID,
          approved: handler === PermitTableActionHandler.APPROVE_PERMIT,
        });
        break;
      default:
        throw new Error(`Unknown action handler: ${handler}`);
    }

    // this lets us return the body so the toast message can show the email or phone number
    let reqBody;
    if (body && typeof body === 'string') {
      reqBody = JSON.parse(body);
    }

    return {
      handler: handler as PermitTableActionHandler,
      status: res.status,
      statusText: res.statusText,
      body: reqBody,
      response: res,
    };
  } catch (error) {
    logError(error as Error);
    return {
      handler: PermitTableActionHandler.UNKNOWN,
      status: 500,
      statusText: 'Internal Server Error',
      body: { error: error instanceof Error ? error.message : 'Unknown error' },
    };
  }
};

const PermitTable = (): ReactNode => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { breakpoints } = theme;
  const { state } = useContext(AppContext);
  const mobile = useMediaQuery(breakpoints.down('lg'));
  const canEdit = state.entityPermissions.permit_type.update;
  const canCreate = state.entityPermissions.permit_type.create;
  const displayToast = useContext(ToastContext);
  const { entity, displayMode } =
    useOutletContext<PanelRootContext<Property | PropertyGroup>>();
  const { _permits, _permitTypes } = useLoaderData() as {
    _permits: Promise<Permit[]>;
    _permitTypes: Promise<PermitType[]>;
  };
  const [permits, setPermits] = useState<Permit[]>([]);
  const [permitTypes, setPermitTypes] = useState<PermitType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPermit, setSelectedPermit] = useState<Permit | null>(null);
  const [showDialog, setShowDialog] = useState<string | null>(null);
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { propertyID } = useParams();
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);

  // toast messages
  const getSuccessMessage = useCallback(
    (response: TraxeroActionResponse<PermitTableActionHandler>, sentTo: string | null): string => {
      switch (response.handler) {
        case PermitTableActionHandler.RESEND_INVITE:
        case PermitTableActionHandler.SEND_INVITE: {
          return t('send_success', {
            ns: 'toasts',
            name: t('invite.label', { ns: 'permits' }),
            email: sentTo,
          });
        }
        case PermitTableActionHandler.SEND_SELF_SERVICE_LINK: {
          return t('send_success', {
            name: t('link', { ns: 'entities' }),
            ns: 'toasts',
            email: sentTo,
          });
        }
        case PermitTableActionHandler.REVOKE_PERMIT:
          return t('revoke_success', {
            name: t('permit.label', { ns: 'entities' }),
            ns: 'toasts',
          });
        case PermitTableActionHandler.APPROVE_PERMIT:
          return t('approve_success', {
            name: t('permit.label', { ns: 'entities' }),
            ns: 'toasts',
          });
        case PermitTableActionHandler.DENY_PERMIT:
          return t('deny_success', {
            name: t('permit.label', { ns: 'entities' }),
            ns: 'toasts',
          });
        default: 
          return t('update_success', {
            ns: 'toasts',
            name: t('permit.label', { ns: 'entities' }),
          });
      }
    },
    [t],
  );
  const getErrorMessage = useCallback(
    (response: TraxeroActionResponse<PermitTableActionHandler>) => {
      switch (response.handler) {
        case PermitTableActionHandler.SEND_INVITE:
        case PermitTableActionHandler.RESEND_INVITE:
          return t('send_error', {
            name: t('invite.label', { ns: 'permits' }),
            ns: 'toasts',
            error: `${response.status}-${
              response.message || response.statusText
            }`,
          });
        case PermitTableActionHandler.SEND_SELF_SERVICE_LINK:
          return t('send_error', {
            name: t('link', { ns: 'entities' }),
            ns: 'toasts',
            error: `${response.status}-${
              response.message || response.statusText
            }`,
          });
        case PermitTableActionHandler.REVOKE_PERMIT:
          return t('revoke_error', {
            name: t('permit.label', { ns: 'entities' }),
            ns: 'toasts',
            error: `${response.status}-${
              response.message || response.statusText
            }`,
          });
        case PermitTableActionHandler.APPROVE_PERMIT:
          return t('approve_error', {
            name: t('permit.label', { ns: 'entities' }),
            ns: 'toasts',
            error: `${response.status}-${
              response.message || response.statusText
            }`,
          });
        case PermitTableActionHandler.DENY_PERMIT:
          return t('deny_error', {
            name: t('permit.label', { ns: 'entities' }),
            ns: 'toasts',
            error: `${response.status}-${
              response.message || response.statusText
            }`,
          });
        default:
          return t('update_error', {
            name: t('permit.label', { ns: 'entities' }),
            ns: 'toasts',
            error: `${response.status}-${
              response.message || response.statusText
            }`,
          });
      }
    },
    [t],
  );


  // Load permits and permit types on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [permitsData, permitTypesData] = await Promise.all([
          _permits,
          _permitTypes,
        ]);
        setPermits(permitsData);
        setPermitTypes(permitTypesData);
      } catch (error) {
        logError(error as Error);
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: t('error.loadingPermits'),
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [_permits, _permitTypes, displayToast, t]);

  // WebSocket integration for real-time updates
  useEffect(() => {
    const pusher = new Pusher(window.PUSHER_KEY, {
      cluster: window.PUSHER_CLUSTER,
    });
    const channel = pusher.subscribe(`tenant-${state.tenant.id}`);

    // NOTE: Websocket permit events contain lightweight permit summaries (not full permit objects)
    // These include only essential fields needed for table display and basic operations:
    // - Basic permit info (id, permitNumber, permitStatus, inviteStatus, startTime, endTime)
    // - Contact info (name, primaryEmail, primaryPhone)
    // - Vehicle info (plate, make, model, year, color)
    // - Property info (id, name)
    // - Permit type info (name)
    // - Tenant info (id, name)
    // Full permit data should be fetched via API for detailed operations
    channel.bind(PPIPusherEvent.PermitCreated, (permit: Permit) => {
      if (permit.property?.id !== entity.id) {
        return;
      }
      displayToast({
        type: TOAST_TYPE.INFO,
        message: t('create_success', {
          ns: 'toasts',
          name: t('permit.label', { ns: 'entities' }),
        }),
      });
      setPermits((current) => [
        permit,
        ...current.filter((p) => p.id !== permit.id),
      ]);
    });

    channel.bind(PPIPusherEvent.PermitUpdated, (permit: Permit) => {
      if (permit.property?.id !== entity.id) {
        return;
      }
      displayToast({
        type: TOAST_TYPE.INFO,
        message: t('update_success', {
          ns: 'toasts',
          name: t('permit.label', { ns: 'entities' }),
        }),
      });
      setPermits((current) => {
        const filteredPermits = current.filter((p) => p.id !== permit.id);
        if (permit.permitStatus === PermitStatus.Disabled) {
          return filteredPermits;
        } else {
          return [permit, ...filteredPermits];
        }
      });
    });

    channel.bind(PPIPusherEvent.PermitList, (permit: Permit) => {
      if (permit.property?.id !== entity.id) {
        return;
      }
      // Reload permits data
      fetcher.load(ROUTES.PROPERTY_PERMITS);
    });

    return () => {
      if (channel) {
        channel.unbind(PPIPusherEvent.PermitCreated);
        channel.unbind(PPIPusherEvent.PermitUpdated);
        channel.unbind(PPIPusherEvent.PermitList);
        pusher?.unsubscribe(`tenant-${state.tenant.id}`);
      }
    };
  }, []);

  useEffect(() => {
    //scroll to the top of the table when the sorting changes
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
    } catch (error) {
      logError(error as Error);
    }
  }, [sorting]);

  useEffect(() => {
    if (fetcher.data) {
      const response =
        fetcher.data as TraxeroActionResponse<PermitTableActionHandler>;
      if (response.status < 400) {
        console.log('response', response);
        const sentTo = response.body?.email || response.body?.phone || response.body?.name;
        const msg = getSuccessMessage(response, sentTo);
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: msg,
        });
        setShowDialog(null);
        setSelectedPermit(null);
      } else {
        const msg = getErrorMessage(response);
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: msg,
        });
      }
    }
  }, [fetcher.data]);

  const handleCloseDialog = (): void => {
    setShowDialog(null);
    setSelectedPermit(null);
  };
  const handleEditPermit = (permit: Permit) => {
    navigate(`${pathname}/${permit.id}`);
  };
  const handleCreatePermit = () => {
    if (permitTypes.length > 0) {
      const defaultPermitType = permitTypes[0];
      const startTime = new Date();
      const endTime = calculateEndOfTimePeriod(
        startTime,
        defaultPermitType.data?.defaultTerm,
      );
      setSelectedPermit({
        ...getBlankPermit(state.tenant.id),
        permitType: defaultPermitType,
        startTime: startTime.getTime(),
        endTime: endTime.getTime(),
      });
      navigate(`${pathname}/0`);
    } else {
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t('toasts.no_permit_types', { ns: 'permits' }),
      });
    }
  };

  const handleLaunchImportWizard = () => {
    if (permitTypes.length > 0) {
      navigate(
        routeWithParams(ROUTES.PROPERTY_PERMITS_IMPORT, [
          state.tenant.id,
          Number(propertyID),
        ]),
      );
    } else {
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t('toasts.no_permit_types', { ns: 'permits' }),
      });
    }
  };
  const handleResendInvite = (permit: Permit) => {
    setSelectedPermit(permit);
    setShowDialog('permitInvite');
  };
  const handleReviewPermit = (permit: Permit) => {
    setSelectedPermit(permit);
    setShowDialog('permitApproval');
  };
  const handleRevokePermit = (permit: Permit) => {
    setSelectedPermit(permit);
    setShowDialog('permitCancel');
  };
  const handleViewRevisions = (id: number) => {
    navigate(
      routeWithParams(ROUTES.REVISIONS, [
        state.tenant.id,
        EntityTypes.Permit,
        id.toString(),
      ]),
    );
  };
  const handleExportSelectedAsCSV = (
    rows: MRT_Row<Permit>[],
  ) => {
    generateCSV(
      rows.map((row) => row.original),
      `${entity.name}_permits_${dayjs().format('YYYYMMDDHHmmss')}.csv`,
      'permit',
    );
  };
  const handleExportSelectedAsPDF = (
    rows: MRT_Row<Permit>[],
  ) => {
    generatePermitPDF(
      rows.map((row) => row.original),
      `${entity.name}_permits_${dayjs().format('YYYYMMDDHHmmss')}.pdf`,
      state.tenant,
    );
  };
  const handleExportAllAsCSV = () => {
    generateCSV(
      permits,
      formatDateRangeReportFileName({
        startDate: dayjs().toDate(),
        endDate: dayjs().toDate(),
        entityType: EntityTypes.Permit,
        ext: 'csv',
      }),
      'permit',
    );
  };
  const handleExportAllAsPDF = () => {
    generatePermitPDF(
      permits,
      formatDateRangeReportFileName({
        startDate: dayjs().toDate(),
        endDate: dayjs().toDate(),
        entityType: EntityTypes.Permit,
        ext: 'pdf',
      }),
      state.tenant,
    );
  };
  const getMenuOptions = useCallback(
    (permit: Permit): MenuOption[] => {
      return [
        {
          content: t('buttons.view', {
            name: t('revision.label_plural'),
          }),
          onClick: () => handleViewRevisions(permit.id),
        },
        {
          content: t('permit.invite.resend'),
          onClick: () => handleResendInvite(permit),
          disabled: !canEdit || permit.inviteStatus === InviteStatus.None,
        },
        {
          content: t('permit.revokeButton'),
          onClick: () => handleRevokePermit(permit),
          disabled:
            !canEdit ||
            permit.permitStatus === PermitStatus.Expired ||
            permit.permitStatus === PermitStatus.Disabled,
        },
      ];
    },
    [t],
  );
  const handlePermitApproval = (permit: Permit) => {
    fetcher.submit(
      {
        handler: PermitTableActionHandler.APPROVE_PERMIT,
        tenantID: state.tenant.id,
        entityType: EntityTypes.Property,
        entityID: permit.property?.id || 0,
        permitID: permit.id,
      },
      {
        method: 'PUT',
        encType: 'application/json',
      },
    );
  };
  const handlePermitDenial = (permit: Permit) => {
    fetcher.submit(
      {
        handler: PermitTableActionHandler.DENY_PERMIT,
        tenantID: state.tenant.id,
        entityType: EntityTypes.Property,
        entityID: permit.property?.id || 0,
        permitID: permit.id,
      },
      {
        method: 'PUT',
        encType: 'application/json',
      },
    );
  };

  const itemHeight = 70;
  const renderItem = useCallback(
    (permit: Permit, index: number): ReactNode => {
      return (
        <ListItem
          key={`permit-${permit.id}-${index}`}
          id={permit.id.toString()}
          onClick={() => handleEditPermit(permit)}
          divider
          secondaryAction={
            <ListItemMenuButton
              menuOptions={[
                {
                  content: t('buttons.resend_invite', { ns: 'permits' }),
                  onClick: () => handleResendInvite(permit),
                  disabled: !canEdit || permit.inviteStatus === InviteStatus.None,
                },
                {
                  content: t('buttons.revoke', { ns: 'permits' }),
                  onClick: () => handleRevokePermit(permit),
                  disabled: !canEdit || permit.permitStatus === PermitStatus.Expired || permit.permitStatus === PermitStatus.Disabled,
                },
              ]}
            />
          }
          sx={{ height: itemHeight, backgroundColor: 'white' }}
        >
          <ListItemText
            primary={`${permit?.contact?.name} (${formatLabel(
              permit.permitStatus,
            )})`}
            secondary={formatLicensePlateString(permit.vehicle?.plate)}
            primaryTypographyProps={{
              variant: 'subtitle1',
              noWrap: true,
            }}
            secondaryTypographyProps={{
              variant: 'subtitle2',
              noWrap: true,
            }}
          />
          <Typography
            variant='subtitle2'
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
            }}
          ></Typography>
        </ListItem>
      );
    },
    [getMenuOptions, handleEditPermit],
  );
  const columns = useMemo<MRT_ColumnDef<Permit>[]>(
    () => [
      {
        header: t('permit.id', { ns: 'entities' }),
        accessorKey: 'id',
      },
      {
        header: t('permit.permitNumber', { ns: 'entities' }),
        accessorKey: 'permitNumber',
      },
      {
        header: t('permit.contact.name', { ns: 'entities' }),
        accessorKey: 'contact.name',
        id: 'contactName',
      },
      {
        header: t('permit.vehicle.plate.label', { ns: 'entities' }),
        id: 'plateNumber',
        accessorKey: 'vehicle.plate.number',
        accessorFn: (row) => formatLicensePlateString(row.vehicle?.plate),
      },
      {
        header: t('permit.vehicle.label', { ns: 'entities' }),
        accessorKey: 'vehicle',
        accessorFn: (row) => formatVehicleString(row.vehicle),
      },
      {
        header: t('permit.inviteStatus', { ns: 'entities' }),
        accessorKey: 'inviteStatus',
        filterVariant: 'select',
        filterSelectOptions: [
          {
            label: t('inviteStatus.pending', { ns: 'enums' }),
            value: InviteStatus.Pending,
          },
          {
            label: t('inviteStatus.sent', { ns: 'enums' }),
            value: InviteStatus.Sent,
          },
          {
            label: t('inviteStatus.failed', { ns: 'enums' }),
            value: InviteStatus.Failed,
          },
          {
            label: t('inviteStatus.none', { ns: 'enums' }),
            value: InviteStatus.None,
          },
        ],
        Cell: ({ row }) => {
          return formatLabel(row.original.inviteStatus);
        },
      },
      {
        header: t('permit.permitStatus', { ns: 'entities' }),
        accessorKey: 'permitStatus',
        filterVariant: 'select',
        filterSelectOptions: [
          {
            label: t('permitStatus.active', { ns: 'enums' }),
            value: PermitStatus.Active,
          },
          {
            label: t('permitStatus.expired', { ns: 'enums' }),
            value: PermitStatus.Expired,
          },
          {
            label: t('permitStatus.pending_registration', { ns: 'enums' }),
            value: PermitStatus.PendingRegistration,
          },
          {
            label: t('permitStatus.pending_approval', { ns: 'enums' }),
            value: PermitStatus.PendingApproval,
          },
          {
            label: t('permitStatus.pending_payment', { ns: 'enums' }),
            value: PermitStatus.PendingPayment,
          },
          {
            label: t('permitStatus.disabled', { ns: 'enums' }),
            value: PermitStatus.Disabled,
          },
          {
            label: t('permitStatus.approval_expired', { ns: 'enums' }),
            value: PermitStatus.ApprovalExpired,
          },
        ],
        Cell: ({ row }) => {
          return formatLabel(row.original.permitStatus);
        },
      },
      {
        header: t('permit.startTime', { ns: 'entities' }),
        accessorKey: 'startTime',
        accessorFn: (row) => new Date(row.startTime),
        filterVariant: 'datetime-range',
        Cell: ({ cell }) => {
          return dayjs(cell.getValue<Date>()).format('MM/DD/YYYY hh:mm a');
        },
      },
      {
        header: t('permit.endTime', { ns: 'entities' }),
        accessorKey: 'endTime',
        accessorFn: (row) => new Date(row.endTime || THE_DISTANT_FUTURE),
        filterVariant: 'datetime-range',
        Cell: ({ cell }) => {
          return dayjs(cell.getValue<Date>()).format('MM/DD/YYYY hh:mm a');
        },
      },
      {
        header: t('permit.permitType.label', { ns: 'entities' }),
        accessorKey: 'permitType.name',
      },
    ],
    [t],
  );
  const tableDefaults = useDefaultTableOptions<Permit>();
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: permits,
    columns,
    enableRowVirtualization: true,
    rowVirtualizerInstanceRef,
    rowVirtualizerOptions: { overscan: 5 },
    enableColumnOrdering: true,
    enableRowSelection: true,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      density: 'compact',
      columnVisibility: {
        id: false,
        permitNumber: true,
        contactName: true,
        plateNumber: true,
        vehicle: false,
        inviteStatus: true,
        permitStatus: true,
        startTime: false,
        endTime: false,
        permitType: true,
      },
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    state: {
      isLoading,
      sorting,
      showLoadingOverlay: false,
    },
    onSortingChange: setSorting,
    getRowId: (row) => `${row.id}`,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        cursor: 'pointer',
        backgroundColor: 'white',
      },
      onClick: () => handleEditPermit(row.original),
    }),
    renderTopToolbar: ({ table }) => (
      <MRTableToolbar
        table={table}
        isSubmitting={fetcher.state === 'submitting'}
        optionsDisabled={!canCreate}
        menuOptions={[
          {
            onClick: handleCreatePermit,
            content: t('buttons.create', {
              name: t('permit.label', { ns: 'entities' }),
            }),
            disabled: !canCreate,
            icon: <Add />,
          },
          {
            content: t('buttons.send_invite', { ns: 'permits' }),
            onClick: () => {
              setShowDialog('permitInvite');
            },
            disabled: !canCreate,
            icon: <Send />,
          },
          {
            content: t('buttons.wizard', { ns: 'permits' }),
            onClick: handleLaunchImportWizard,
            disabled: !canCreate || displayMode !== 'property',
            icon: <AutoAwesome />,
          },
          {
            onClick: () => handleExportAllAsCSV(),
            content: t('buttons.exportAllCsv'),
            icon: <FileDownload />,
            disabled: permits.length === 0 || isLoading,
          },
          {
            onClick: () => handleExportAllAsPDF(),
            content: t('buttons.exportAllPdf'),
            icon: <FileDownload />,
            disabled: permits.length === 0 || isLoading,
          },
          {
            content: t('buttons.exportSelectedCsv'),
            icon: <FileDownload />,
            disabled:
              !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected(),
            onClick: () => handleExportSelectedAsCSV(table.getSelectedRowModel().rows),
          },
          {
            content: t('buttons.exportSelectedPdf'),
            icon: <FileDownload />,
            disabled:
              !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected(),
            onClick: () => handleExportSelectedAsPDF(table.getSelectedRowModel().rows),
          },
        ]}
      />
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => [
      <MenuItem
        key={1}
        divider
        disabled={
          row.original.permitStatus === PermitStatus.Expired ||
          row.original.permitStatus === PermitStatus.Disabled
        }
        onClick={() => {
          handleRevokePermit(row.original);
          closeMenu();
        }}
        sx={{ m: 0, backgroundColor: 'white' }}
      >
        <ListItemIcon>
          <Cancel />
        </ListItemIcon>
        {t('buttons.revoke', { ns: 'permits' })}
      </MenuItem>,
      <MenuItem
        key={2}
        divider
        onClick={() => {
          handleResendInvite(row.original);
          closeMenu();
        }}
        disabled={row.original.inviteStatus === InviteStatus.None}
        sx={{ m: 0, backgroundColor: 'white' }}
      >
        <ListItemIcon>
          <Send />
        </ListItemIcon>
        {t('buttons.resend_invite', { ns: 'permits' })}
      </MenuItem>,
      <MenuItem
        key={3}
        divider
        onClick={() => {
          handleReviewPermit(row.original);
          closeMenu();
        }}
        sx={{ m: 0, backgroundColor: 'white' }}
        disabled={row.original.permitStatus !== PermitStatus.PendingApproval}
      >
        <ListItemIcon>
          <Approval />
        </ListItemIcon>
        {t('dialogs.approval.review', { ns: 'permits' })}
      </MenuItem>,
      <MenuItem
        key={4}
        divider
        onClick={() => {
          handleViewRevisions(row.original.id);
          closeMenu();
        }}
        sx={{ m: 0, backgroundColor: 'white' }}
      >
        <ListItemIcon>
          <History />
        </ListItemIcon>
        {t('buttons.view', { name: t('revision.label_plural') })}
      </MenuItem>,
    ],
  });

  return (
    <>
      {showDialog === 'permitInvite' && (
        <PermitInviteDialog
          isOpen={showDialog === 'permitInvite'}
          handleCancel={handleCloseDialog}
          entity={entity}
          permit={selectedPermit}
          fetcherKey={fetcherKey}
          permitTypeOptions={permitTypes}
          displayMode={displayMode}
        />
      )}
      {showDialog === 'permitCancel' && selectedPermit && (
        <PermitCancelDialog
          open={showDialog === 'permitCancel'}
          handleCancel={handleCloseDialog}
          permit={selectedPermit}
          fetcherKey={fetcherKey}
          displayMode={displayMode}
        />
      )}
      {showDialog === 'permitApproval' && selectedPermit && (
        <PermitReviewDialog
          isOpen={showDialog === 'permitApproval'}
          handleCancel={handleCloseDialog}
          handlePermitApproval={handlePermitApproval}
          handlePermitDenial={handlePermitDenial}
          permit={selectedPermit}
        />
      )}
      {mobile ? (
        <MobilePanelList<Permit>
          data={permits}
          columns={columns.map((c) => ({
            name: c.header,
            searchKey: c.accessorKey,
          }))}
          renderItem={renderItem}
          itemHeight={itemHeight}
          title={t('permit.label_plural')}
          withSearchBar
          menuOptions={[
            {
              onClick: handleCreatePermit,
              content: t('buttons.create', { name: t('permit.label') }),
              disabled: !canCreate,
              icon: <Add />,
            },
            {
              content: t('permit.invite.menu'),
              onClick: () => {
                setShowDialog('permitInvite');
              },
              disabled: !canCreate,
              icon: <Send />,
            },
          ]}
        />
      ) : (
        <MaterialReactTable table={table} />
      )}
    </>
  );
};

export default PermitTable;
